import { useEffect, useRef, useState } from 'react';
import { motion } from 'motion/react';
import Link from 'next/link';
import Image from 'next/image';
import Container from 'components/container';

import { PrimaryLink } from 'shared/styles/components';
import { useTheme } from 'shared/styles/theme';
import { LIBUM_DOCS_NEXUM, LIBUM_DOCS_POWERON_PRO, LIBUM_KEYWORDS, LIBUM_URL } from 'shared/constants/globals';

import {
  LandingActions,
  LandingAnnouncement,
  LandingBody,
  LandingColumn,
  LandingColumns,
  LandingContainer,
  LandingLogos,
  LandingRow,
} from './landing.styles';

import HOVER_ARROW from 'public/icons/hover_arrow.svg';

import ALTURA_CU_LOGO from 'public/logos/orgs/altura_cu.svg';
import BCU_LOGO from 'public/logos/orgs/bcu.svg';
import BIDWIZER from 'public/logos/orgs/bidwizer.svg';
import JH_LOGO from 'public/logos/orgs/jack_henry.svg';
import MAX_LOGO from 'public/logos/orgs/max_cu.svg';
import PIONEERA_FCU from 'public/logos/orgs/pioneera_fcu.svg';
import TWINSTAR_CU_LOGO from 'public/logos/orgs/twinstar_cu.svg';

const ALL_LOGOS = [
  { Component: ALTURA_CU_LOGO, name: 'Altura CU', height: 22 },
  { Component: BCU_LOGO, name: 'BCU', height: 20 },
  { Component: BIDWIZER, name: 'Bidwizer', height: 20 },
  { Component: JH_LOGO, name: 'Jack Henry', height: 30 },
  { Component: MAX_LOGO, name: 'MAX CU', height: 30 },
  { Component: PIONEERA_FCU, name: 'Pioneera FCU', height: 30 },
  { Component: TWINSTAR_CU_LOGO, name: 'Twinstar CU', height: 30 },
];

const VISIBLE_LOGOS_COUNT = 4;
const ROTATION_INTERVAL = 2500; // milliseconds

const TYPING_WORDS = [
  'developers',
  'leaders',
  'innovators',
  'visionaries',
  'engineers',
  'designers',
  'ops teams',
  'lending teams',
  'frontline staff',
  'member services',
  'compliance teams',
  'data analysts',
  'product managers',
  'IT teams',
  'executives',
  'loan officers',
  'back office',
];
const TYPING_SPEED = 100; // ms per character
const PAUSE_BEFORE_TYPING = 1000; // pause before typing new word
const PAUSE_DURATION = 2000; // pause before deleting
const DELETE_SPEED = 50; // ms per character when deleting

function getRandomLogos(count: number, exclude: number[] = []): number[] {
  const available = Array.from({ length: ALL_LOGOS.length }, (_, i) => i).filter((i) => !exclude.includes(i));
  const shuffled = available.sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

export default function Landing() {
  const { theme } = useTheme();
  const [logoIndices, setLogoIndices] = useState<number[]>([0, 1, 2, 3]);
  const [exitingSlots, setExitingSlots] = useState<Set<number>>(new Set());
  const [enteringSlots, setEnteringSlots] = useState<Set<number>>(new Set());

  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const typingContentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | 'auto'>('auto');

  // Measure content height when text changes
  useEffect(() => {
    if (typingContentRef.current) {
      setContentHeight(typingContentRef.current.offsetHeight);
    }
  }, [currentText]);

  // Typing animation effect
  useEffect(() => {
    const currentWord = TYPING_WORDS[currentWordIndex];

    const handleTyping = () => {
      if (!isDeleting) {
        // Typing
        if (currentText.length < currentWord.length) {
          setCurrentText(currentWord.substring(0, currentText.length + 1));
        } else {
          // Finished typing, pause then start deleting
          setTimeout(() => setIsDeleting(true), PAUSE_DURATION);
          return;
        }
      } else {
        // Deleting
        if (currentText.length > 0) {
          setCurrentText(currentWord.substring(0, currentText.length - 1));
        } else {
          // Finished deleting, move to random word (not the same as current)
          setIsDeleting(false);
          setCurrentWordIndex((prev) => {
            let nextIndex;
            do {
              nextIndex = Math.floor(Math.random() * TYPING_WORDS.length);
            } while (nextIndex === prev && TYPING_WORDS.length > 1);
            return nextIndex;
          });
          return;
        }
      }
    };

    // Determine delay: pause before typing if starting new word, otherwise use typing/delete speed
    const delay =
      !isDeleting && currentText.length === 0 ? PAUSE_BEFORE_TYPING : isDeleting ? DELETE_SPEED : TYPING_SPEED;

    const timeout = setTimeout(handleTyping, delay);

    return () => clearTimeout(timeout);
  }, [currentText, isDeleting, currentWordIndex]);

  // Logo rotation effect
  useEffect(() => {
    // Initialize with random logos after mount to avoid hydration mismatch
    setLogoIndices(getRandomLogos(VISIBLE_LOGOS_COUNT));

    let lastSlot = -1;

    const interval = setInterval(() => {
      // Pick a random slot to update, but not the same as last time
      let nextSlot;
      do {
        nextSlot = Math.floor(Math.random() * VISIBLE_LOGOS_COUNT);
      } while (nextSlot === lastSlot);
      lastSlot = nextSlot;

      // Mark slot as exiting
      setExitingSlots((prev) => new Set(prev).add(nextSlot));

      // After exit animation, update logo and mark as entering
      setTimeout(() => {
        setLogoIndices((currentIndices) => {
          // Exclude both the current logo in this slot AND all other displayed logos
          const currentLogoInSlot = currentIndices[nextSlot];
          const otherDisplayedLogos = currentIndices.filter((_, idx) => idx !== nextSlot);
          const excludeList = [...otherDisplayedLogos, currentLogoInSlot];
          const newLogoIndex = getRandomLogos(1, excludeList)[0];
          const newIndices = [...currentIndices];
          newIndices[nextSlot] = newLogoIndex;
          return newIndices;
        });

        // Remove exiting state and add entering state
        setExitingSlots((prev) => {
          const newSet = new Set(prev);
          newSet.delete(nextSlot);
          return newSet;
        });

        setEnteringSlots((prev) => new Set(prev).add(nextSlot));

        // Remove entering state after animation completes
        setTimeout(() => {
          setEnteringSlots((prev) => {
            const newSet = new Set(prev);
            newSet.delete(nextSlot);
            return newSet;
          });
        }, 400);
      }, 400); // Exit animation duration
    }, ROTATION_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, []);
  const title = 'Libum | Making Credit Union Software a Piece of Cake';
  const description =
    'Libum is a software company driving the modernization of credit unions. Service your members faster with NexumAPI, Glaci, or PowerOn Pro.';

  return (
    <Container
      title={title}
      description={description}
      keywords={LIBUM_KEYWORDS}
      url={`${LIBUM_URL}/`}
      socialCard="libum"
    >
      <LandingContainer>
        <LandingBody>
          <LandingColumns className="ltr">
            <LandingColumn className="left">
              <div className="image-container composite">
                <Link href="/poweron-pro" className="main-image-link">
                  <Image
                    className="poweron-pro"
                    src={
                      theme === 'dark'
                        ? '/images/3a8f2d1e-7c4b-4e9a-b5f6-8d2e1c3a4b5f.png'
                        : '/images/e7a3c1f5-9d2b-4e6f-8a0c-3b5d7f9e1a2c.png'
                    }
                    alt="PowerOn Pro in Visual Studio Code with a PowerOn file open"
                    height={357}
                    width={580}
                    priority={true}
                    quality={85}
                    style={{ borderRadius: '8px' }}
                  />
                </Link>
                <Link href="/glaci" className="overlay-image glaci">
                  <Image
                    src={
                      theme === 'dark'
                        ? '/images/2b4d6e8f-1a3c-5e7d-9b1f-3c5e7d9b1f3a.png'
                        : '/images/c4f8e2a1-5b7d-9e3f-1c6a-8d2b4f0e7a9c.png'
                    }
                    alt="Glaci Data Analytics Dashboard"
                    width={151}
                    height={258}
                    loading="lazy"
                    quality={85}
                    style={{ borderRadius: '8px' }}
                  />
                </Link>
                <Link href="/nexum" className="overlay-image nexum">
                  <Image
                    src={
                      theme === 'dark'
                        ? '/images/9e7d6c5b-4a3f-2e1d-8c7b-6a5f4e3d2c1b.png'
                        : '/images/d5e6f7a8-2b3c-4d5e-6f7a-8b9c0d1e2f3a.png'
                    }
                    alt="NexumAPI JSON/REST Interface Documentation"
                    width={300}
                    height={162}
                    loading="lazy"
                    quality={85}
                    style={{ borderRadius: '8px' }}
                  />
                </Link>
                <div className="dot-mask"></div>
              </div>
            </LandingColumn>
            <LandingColumn className="right poweron-pro">
              <LandingRow>
                <h1>
                  <span>Building CU software</span>
                  <motion.div
                    className="typing-line"
                    animate={{ height: contentHeight }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                  >
                    <div ref={typingContentRef} className="typing-content">
                      for <span className="accent">&gt;</span> <span className="typing-text">{currentText}</span>
                    </div>
                  </motion.div>
                </h1>
                <p>Modern apps, tools, and APIs that make the Jack Henry core work better for your credit union.</p>
              </LandingRow>
              <LandingRow>
                <Link href="/products">
                  <PrimaryLink className="landing-button">View Products</PrimaryLink>
                </Link>
              </LandingRow>
              <LandingRow>
                <LandingAnnouncement>
                  <span className="tag new">New</span>
                  <Link href="/blog/the-only-symitar-quest-overlay">
                    Symitar Quest Just Got an Overlay
                    <HOVER_ARROW />
                  </Link>
                </LandingAnnouncement>
                <LandingActions>
                  <Link href={`${LIBUM_DOCS_POWERON_PRO}/quickstart`} target="_blank">
                    Write PowerOn in VS Code
                  </Link>
                  <Link href={`${LIBUM_DOCS_NEXUM}/quickstart`} target="_blank">
                    Use JSON with SymX
                  </Link>
                </LandingActions>
                <LandingLogos>
                  <p>Loved by hundreds of teams</p>
                  <div className="logo-wall">
                    {logoIndices.map((logoIndex, slotIndex) => {
                      const logoData = ALL_LOGOS[logoIndex];
                      const Logo = logoData.Component;
                      const isExiting = exitingSlots.has(slotIndex);
                      const isEntering = enteringSlots.has(slotIndex);
                      return (
                        <div key={slotIndex} className="logo-slot">
                          <div className={`logo-item ${isExiting ? 'exiting' : ''} ${isEntering ? 'entering' : ''}`}>
                            <Logo style={{ height: `${logoData.height}px` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </LandingLogos>
              </LandingRow>
            </LandingColumn>
          </LandingColumns>
        </LandingBody>
      </LandingContainer>
    </Container>
  );
}
